/**
 * Badge size tokens shared by the hen-ui Tailwind config and the configs that
 * spread it.
 *
 * Each size class (`badge-size-{size}`) sets six Badge-local variables
 * (`--hen-badge-*`) and the Badge base utilities read them, so a size, or a
 * breakpoint override of one, switches type, padding, height and icon size
 * together.
 *
 * Each size has default values, and a theme can tune any size through its
 * `--cds-badge-{size}-*` tokens, which every value reads first. Authors pick
 * a size per badge. Without a size token, `sm` and `lg` fall back to exactly what
 * they rendered before per-size values existed, so a tenant opts in to new
 * values through its own tokens. `xl` follows the design system's XL badge
 * and falls back to code defaults only: the shared `--cds-badge-*` sizing
 * tokens have always tuned the `sm` badge, and a tenant that set them for
 * `sm` should not move `xl`.
 */

export const BADGE_SIZES = ['sm', 'lg', 'xl'] as const;
export type BadgeSize = (typeof BADGE_SIZES)[number];

export const BADGE_SIZE_TOKEN_KEYS = [
  'font-size',
  'line-height',
  'padding-x',
  'padding-y',
  'min-height',
  'icon-size',
] as const;
export type BadgeSizeTokenKey = (typeof BADGE_SIZE_TOKEN_KEYS)[number];

type BadgeSizeValues = Record<BadgeSizeTokenKey, string>;

const SHARED_PADDING_Y = 'var(--cds-badge-padding-y, 4px)';
const SHARED_ICON_SIZE = 'var(--cds-badge-icon-size, 1rem)';

const BADGE_SIZE_FALLBACKS: Record<BadgeSize, BadgeSizeValues> = {
  sm: {
    'font-size': 'var(--cds-badge-font-size, var(--font-size-l2, 10px))',
    'line-height': 'var(--cds-badge-line-height, 1)',
    'padding-x': 'var(--cds-badge-padding-x, var(--space-xxs, 8px))',
    'padding-y': SHARED_PADDING_Y,
    'min-height': 'auto',
    'icon-size': SHARED_ICON_SIZE,
  },
  lg: {
    'font-size': 'var(--font-size-l1, 12px)',
    'line-height': 'var(--line-height-l1, 16px)',
    'padding-x': 'var(--space-xs, 12px)',
    'padding-y': SHARED_PADDING_Y,
    'min-height': 'auto',
    'icon-size': SHARED_ICON_SIZE,
  },
  xl: {
    'font-size': 'var(--font-size-b5, 14px)',
    'line-height': 'var(--line-height-b5, 22px)',
    'padding-x': '12px',
    'padding-y': '4px',
    'min-height': '30px',
    'icon-size': '13px',
  },
};

const mapKeys = (value: (key: BadgeSizeTokenKey) => string): BadgeSizeValues =>
  Object.fromEntries(
    BADGE_SIZE_TOKEN_KEYS.map((key) => [key, value(key)]),
  ) as BadgeSizeValues;

export const badgeSizeTokens: Record<BadgeSize, BadgeSizeValues> =
  Object.fromEntries(
    BADGE_SIZES.map((size) => [
      size,
      mapKeys(
        (key) =>
          `var(--cds-badge-${size}-${key}, ${BADGE_SIZE_FALLBACKS[size][key]})`,
      ),
    ]),
  ) as Record<BadgeSize, BadgeSizeValues>;

const localVariable = (key: BadgeSizeTokenKey) => `--hen-badge-${key}`;

/** Reads a local variable; a badge with no size class renders as `sm`. */
const readLocal = (key: BadgeSizeTokenKey) =>
  `var(${localVariable(key)}, ${badgeSizeTokens.sm[key]})`;

/** `addUtilities` input: one `.badge-size-{size}` rule per size. */
export const badgeSizeUtilities: Record<
  string,
  Record<string, string>
> = Object.fromEntries(
  BADGE_SIZES.map((size) => [
    `.badge-size-${size}`,
    Object.fromEntries(
      BADGE_SIZE_TOKEN_KEYS.map((key) => [
        localVariable(key),
        badgeSizeTokens[size][key],
      ]),
    ),
  ]),
);

export const badgeSizeThemeExtend = {
  spacing: {
    'badge-size-x': readLocal('padding-x'),
    'badge-size-y': readLocal('padding-y'),
  },
  fontSize: {
    'badge-size': [readLocal('font-size'), readLocal('line-height')] as [
      string,
      string,
    ],
  },
  minHeight: {
    'badge-size': readLocal('min-height'),
  },
  size: {
    'badge-size-icon': readLocal('icon-size'),
  },
};
